import React, { useEffect, useState } from "react";
import useAxios from "../utils/useAxios";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaTimes,
  FaKey,
  FaUserTag
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/EmployeesPage';

const RolesPage = () => {
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const { sidebarVisible } = useSidebar();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    nom: "",
    description: ""
  });

  const [formDataEdit, setFormDataEdit] = useState({
    nom: "",
    description: ""
  });

  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const api = useAxios();

  // Charger les organisations
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const res = await api.get('/organisations/');
        setOrganisations(res.data);
      } catch (error) {
        Swal.fire('Erreur', 'Impossible de charger les organisations', 'error');
      }
    };
    fetchOrganisations();
  }, []);

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchRoles();
      fetchAllPermissions();
    }
  }, [selectedOrganisation]);

  const fetchRoles = async () => {
    try {
      const response = await api.get(`/roles/?organisation=${selectedOrganisation}`);
      setRoles(response.data);
    } catch (error) {
      console.error("Erreur API:", error);
      setError("Impossible de récupérer les rôles.");
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const response = await api.get('/permissions/');
      setAllPermissions(response.data);
    } catch (error) {
      console.error("Erreur API:", error);
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.nom.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditChange = (e) => {
    setFormDataEdit({ ...formDataEdit, [e.target.name]: e.target.value });
  };

  const handlePermissionToggle = (permissionId) => {
    if (selectedPermissions.includes(permissionId)) {
      setSelectedPermissions(selectedPermissions.filter(id => id !== permissionId));
    } else {
      setSelectedPermissions([...selectedPermissions, permissionId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOrganisation) {
      Swal.fire('Erreur', 'Veuillez sélectionner une organisation', 'error');
      return;
    }

    try {
      const response = await api.post("/roles/create/", {
        ...formData,
        organisation: selectedOrganisation
      });

      if (response.data) {
        setRoles((prev) => [...prev, response.data]);
        setShowModal(false);
        setFormData({ nom: "", description: "" });
        Swal.fire({
          title: "Rôle ajouté avec succès",
          icon: "success",
          toast: true,
          timer: 3000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        throw new Error("Données invalides reçues de l'API.");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      Swal.fire({
        title: "Échec de l'ajout",
        text: error.response?.data?.detail || "Une erreur s'est produite",
        icon: "error",
        toast: true,
        timer: 3000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleView = (role) => {
    setSelectedRole(role);
    setShowViewModal(true);
  };

  const handleEdit = (role) => {
    setFormDataEdit({
      nom: role.nom,
      description: role.description || ""
    });
    setSelectedRole(role);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
  
    if (!formDataEdit.nom) {
      Swal.fire({
        title: "Erreur",
        text: "Le nom du rôle est obligatoire.",
        icon: "error",
        toast: true,
        timer: 3000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
      return;
    }
  
    try {
      const response = await api.put(
        `/roles/${selectedRole.id}/update/`,
        formDataEdit
      );
  
      if (response.data) {
        setRoles((prev) =>
          prev.map((role) =>
            role.id === response.data.id ? response.data : role
          )
        );
        setShowEditModal(false);
  
        Swal.fire({
          title: "Rôle modifié avec succès",
          icon: "success",
          toast: true,
          timer: 3000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        throw new Error("Données invalides reçues de l'API.");
      }
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      Swal.fire({
        title: "Échec de la modification",
        text: error.response?.data?.detail || "Une erreur s'est produite",
        icon: "error",
        toast: true,
        timer: 3000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handlePermissions = (role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions ? role.permissions.map(p => p.id) : []);
    setShowPermissionsModal(true);
  };

  const handlePermissionsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.post(
        `/roles/${selectedRole.id}/assign-permissions/`,
        { permissions: selectedPermissions }
      );
  
      if (response.data) {
        setRoles((prev) =>
          prev.map((role) =>
            role.id === selectedRole.id 
              ? { ...role, permissions: response.data.permissions } 
              : role
          )
        );
        setShowPermissionsModal(false);
  
        Swal.fire({
          title: "Permissions mises à jour avec succès",
          icon: "success",
          toast: true,
          timer: 3000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        throw new Error("Données invalides reçues de l'API.");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour des permissions:", error);
      Swal.fire({
        title: "Échec de la mise à jour des permissions",
        text: error.response?.data?.detail || "Une erreur s'est produite",
        icon: "error",
        toast: true,
        timer: 3000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Supprimer ce rôle ?",
      text: "Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: 'Annuler',

      confirmButtonText: "Oui, supprimer",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/roles/${id}/delete/`);
        setRoles((prev) => prev.filter((role) => role.id !== id));
        Swal.fire("Supprimé !", "Le rôle a été supprimé.", "success");
      } catch (error) {
        console.error("Erreur suppression:", error);
        Swal.fire("Erreur", "Impossible de supprimer le rôle.", "error");
      }
    }
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      
      {/* Header avec sélecteur d'organisation */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Gestion des rôles pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={styles.pageTitle}>
            <FaUserTag style={{ marginRight: "10px" }} /> RÔLES - {currentOrganisation.nom}
          </h1>

          <div style={styles.actionBar}>
            {(user?.is_super_admin || user?.permissions?.includes("CREATE_ROLE")) && (
              <button
                onClick={() => setShowModal(true)}
                style={styles.addButton}
              >
                <FaPlus style={{ marginRight: "8px" }} /> Nouveau rôle
              </button>
            )}
            <div style={styles.searchContainer}>
              <FaSearch style={{ marginRight: "8px", color: "#1B019B" }} />
              <input
                type="text"
                placeholder="Rechercher un rôle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            <span style={styles.employeeCount}>
              {roles.length} rôle(s)
            </span>
          </div>

          {error ? (
            <p style={{ color: "red" }}>{error}</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead style={styles.tableHeader}>
                  <tr>
                    <th>Nom</th>
                    <th>Description</th>
                    <th>Permissions</th>
                    <th>Actions</th> 
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.length > 0 ? (
                    filteredRoles.map((role) => (
                      <tr key={role.id}>
                        <td style={{ fontWeight: "bold" }}>{role.nom}</td>
                        <td>{role.description || "Aucune description"}</td>
                        <td>
                          {role.permissions && role.permissions.length > 0 
                            ? `${role.permissions.length} permission(s)` 
                            : "Aucune permission"}
                        </td>
                        <td style={styles.actionButtons}>
                          {(user?.is_super_admin || user?.permissions?.includes("VIEW_ROLE")) && (
                            <button
                              style={styles.viewButton}
                              title="Voir"
                              onClick={() => handleView(role)}
                            >
                              <FaEye />
                            </button>
                          )}
                          {(user?.is_super_admin || user?.permissions?.includes("UPDATE_ROLE")) && (
                            <button
                              style={styles.editButton}
                              title="Modifier"
                              onClick={() => handleEdit(role)}
                            >
                              <FaEdit />
                            </button>
                          )}
                          {(user?.is_super_admin || user?.permissions?.includes("UPDATE_ROLE")) && (
                            <button
                              style={styles.permissionsButton}
                              title="Gérer les permissions"
                              onClick={() => handlePermissions(role)}
                            >
                              <FaKey />
                            </button>
                          )}
                          {(user?.is_super_admin || user?.permissions?.includes("DELETE_ROLE")) && (
                            <button
                              style={styles.deleteButton}
                              title="Supprimer"
                              onClick={() => handleDelete(role.id)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={styles.noData}>
                        Aucun rôle trouvé pour cette organisation.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal d'ajout de rôle */}
          {showModal && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => setShowModal(false)} style={styles.closeButton}>
                  <FaTimes />
                </button>
                <h2 style={styles.modalTitle}>
                  Créer un nouveau rôle - {currentOrganisation.nom}
                </h2>
                <form onSubmit={handleSubmit}>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label htmlFor="nom" style={styles.label}>Nom du rôle *</label>
                      <input 
                        type="text" 
                        name="nom" 
                        value={formData.nom} 
                        onChange={handleChange} 
                        required 
                        style={styles.input} 
                      />
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label htmlFor="description" style={styles.label}>Description</label>
                      <textarea 
                        name="description" 
                        value={formData.description} 
                        onChange={handleChange} 
                        style={{...styles.input, minHeight: "100px"}} 
                        rows="3"
                      />
                    </div>
                  </div>

                  <div style={styles.formSubmit}>
                    <button type="submit" style={styles.submitButton}>
                      Créer le rôle
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal de visualisation de rôle */}
          {showViewModal && selectedRole && (
            <div style={styles.modalOverlay}>
              <div style={styles.viewModalContent}>
                <button onClick={() => setShowViewModal(false)} style={styles.closeButton}>
                  <FaTimes />
                </button>
                <h2 style={styles.modalTitle}>
                  Détails du rôle
                </h2>
                
                <p><strong>Nom :</strong> {selectedRole.nom}</p>
                <p><strong>Description :</strong> {selectedRole.description || "Aucune description"}</p>
                
                <div style={{ marginTop: "20px" }}>
                  <h3 style={{ marginBottom: "10px" }}>Permissions associées :</h3>
                  {selectedRole.permissions && selectedRole.permissions.length > 0 ? (
                    <ul style={{ paddingLeft: "20px" }}>
                      {selectedRole.permissions.map(permission => (
                        <li key={permission.id} style={{ marginBottom: "5px" }}>
                          {permission.code} - {permission.description}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Aucune permission associée à ce rôle.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal de modification de rôle */}
          {showEditModal && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => setShowEditModal(false)} style={styles.closeButton}>
                  <FaTimes />
                </button>

                <h2 style={styles.modalTitle}>
                  Modifier le rôle
                </h2>

                <form onSubmit={handleEditSubmit}>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Nom du rôle *</label>
                      <input 
                        type="text" 
                        name="nom" 
                        value={formDataEdit.nom} 
                        onChange={handleEditChange} 
                        required 
                        style={styles.input} 
                      />
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Description</label>
                      <textarea 
                        name="description" 
                        value={formDataEdit.description} 
                        onChange={handleEditChange} 
                        style={{...styles.input, minHeight: "100px"}} 
                        rows="3"
                      />
                    </div>
                  </div>

                  <div style={styles.formSubmit}>
                    <button type="submit" style={styles.submitButton}>
                      Modifier le rôle
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal de gestion des permissions */}
          {showPermissionsModal && selectedRole && (
            <div style={styles.modalOverlay}>
              <div style={{...styles.modalContent, width: "700px", display: "flex", flexDirection: "column", maxHeight: "90vh"}}>
                <button onClick={() => setShowPermissionsModal(false)} style={styles.closeButton}>
                  <FaTimes />
                </button>

                <h2 style={styles.modalTitle}>
                  Gérer les permissions du rôle: {selectedRole.nom}
                </h2>

                <form onSubmit={handlePermissionsSubmit} style={{ display: "flex", flexDirection: "column", flex: 1 }}>

                  <div style={{ flex: 1, overflowY: "auto", marginBottom: "15px" }}>
                    
                    <div style={{ marginBottom: "15px" }}>
                      <label style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                        <input
                          type="checkbox"
                          checked={selectedPermissions.length === allPermissions.length}
                          onChange={() => {
                            if (selectedPermissions.length === allPermissions.length) {
                              setSelectedPermissions([]); // désélectionner tout
                            } else {
                              setSelectedPermissions(allPermissions.map(p => p.id)); // tout sélectionner
                            }
                          }}
                          style={{ marginRight: "10px" }}
                        />
                        Tout sélectionner / désélectionner
                      </label>
                    </div>

                    <div style={styles.formRow}>
                      {allPermissions.map(permission => (
                        <div key={permission.id} style={{...styles.formGroup, flex: "1 1 48%", marginBottom: "10px"}}>
                          <label style={{display: "flex", alignItems: "center"}}>
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(permission.id)}
                              onChange={() => handlePermissionToggle(permission.id)}
                              style={{marginRight: "10px"}}
                            />
                            <div>
                              <div style={{fontWeight: "bold"}}>{permission.code}</div>
                              <div style={{fontSize: "0.9em", color: "#666"}}>{permission.description}</div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

               
                  <div style={{ borderTop: "1px solid #ddd", paddingTop: "10px", textAlign: "right" }}>
                    <button type="submit" style={styles.submitButton}>
                      Enregistrer les permissions
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={styles.noOrganisation}>
          <h4 style={{ color: '#666', marginBottom: '10px' }}>Aucune organisation sélectionnée</h4>
          <p style={{ color: '#999' }}>Veuillez sélectionner une organisation pour gérer ses rôles</p>
        </div>
      )}
    </div>
  );
};

export default RolesPage;